import React, { useEffect, useState, useCallback } from 'react';
import { Box, CircularProgress, Alert } from '@mui/material';
import { Storage } from '../../Types/Storage';
import { StorageHealthConfig, StorageHealthSetupInput } from '../../Types/StorageHealth';
import SetupMode from './SetupMode';
import MonitorMode from './MonitorMode/MonitorMode';
import API from '../../api2';
import useNotification from '../../Hooks/useNotification';
import { useStorageStore } from '../../Stores/StorageStore';

interface HealthWidgetBaseProps { storage: Storage; }

// Helper to determine if we're within setup window (Sept 1 or if no config yet)
const isSetupSeason = () => {
    const now = new Date();
    return now.getMonth() === 8 && now.getDate() === 1; // September is 8 (0-indexed)
};

const HealthWidgetBase: React.FC<HealthWidgetBaseProps> = ({ storage }) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [config, setConfig] = useState<StorageHealthConfig | null>(null);
    const [forceSetup, setForceSetup] = useState(false); // becomes false after save
    const [monitorData, setMonitorData] = useState<any>(null);
    const notify = useNotification();
    const { setRecentServices, setStorageHealthData } = useStorageStore();

    const loadConfig = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await API.getStorageHealthConfig({ locationId: storage.id });
            console.log(result)
            
            // Check if we have a config (even if inspection data is missing)
            if (result.config) {
                setConfig(result.config);
                setMonitorData(result);
                setForceSetup(false);

                // Load recent services into the store if available
                if (result.recentServices && Array.isArray(result.recentServices)) {
                    setRecentServices(storage.id, result.recentServices);
                }

                // Calculate and store health data
                const healthIndex = result.healthIndex?.finalHealthIndex ?? null;
                const bays = result.inspectionData?.bays;
                let capacity: number | null = null;

                if (bays && Array.isArray(bays) && bays.length > 0) {
                    const validFillLevels = bays
                        .map((bay: any) => bay.currentFillLevel)
                        .filter((level: any): level is number =>
                            typeof level === 'number' && !isNaN(level)
                        );

                    if (validFillLevels.length > 0) {
                        const sum = validFillLevels.reduce((acc: number, val: number) => acc + val, 0);
                        capacity = sum / validFillLevels.length;
                    }
                }

                // Store health data including iotData for alarm notifications
                setStorageHealthData(storage.id, {
                    healthIndex,
                    capacity,
                    iotData: result.iotData ?? null
                });
            } else {
                // No config exists yet, show setup mode
                setConfig(null);
                setForceSetup(true);
                setStorageHealthData(storage.id, { healthIndex: null, capacity: null, iotData: null });
            }
        } catch (e: any) {
            console.warn('Storage health config fetch error:', e?.message);
            setConfig(null);
            setForceSetup(true);
            setStorageHealthData(storage.id, { healthIndex: null, capacity: null, iotData: null });
        } finally {
            setLoading(false);
        }
    }, [storage.id, setRecentServices, setStorageHealthData]);

    useEffect(() => { loadConfig(); }, [loadConfig]);

    const handleSave = async (data: StorageHealthSetupInput) => {
        setSaving(true);
        setError(null);
        const result = await API.saveStorageHealthConfig(data);
        notify(result.message, result.error ? 'error' : 'success');
        const formattedData = {
            ...result.data,
            lastUpdated: new Date(result.data.lastUpdated).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            }) || null,
        };
        setConfig(formattedData);
        setForceSetup(false);
        setSaving(false);
    };

    const showSetup = forceSetup || !config || isSetupSeason();
    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    if (showSetup) {
        return <SetupMode storage={storage} onSave={handleSave} saving={saving} initial={config || undefined} />;
    }

    return <MonitorMode storage={storage} monitorData={monitorData} onUpdate={handleSave} />;
};

export default HealthWidgetBase;